// src/components/WhatsAppTemplatesDialog/WhatsAppTemplatesDialog.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Backdrop,
  CircularProgress,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  FormControl,
} from "@mui/material";

const BUSINESS_ID = "1397371488248383";
const GRAPH_API_VERSION = "v16.0";
const GRAPH_TOKEN = process.env.REACT_APP_WHATSAPP_GRAPH_TOKEN;

function norm(s) {
  return (s || "").toString().trim().toLowerCase();
}

function WhatsAppTemplatesDialog({
  open,
  type,
  templateData = {},
  onClose,
  onSave,
  // NEW: set of already-used template names (normalized, case-insensitive)
  usedTemplateNameSet = new Set(),
}) {
  // form fields
  const [templateName, setTemplateName] = useState("");
  const [campaignUrl, setCampaignUrl] = useState("");
  const [utmSource, setUtmSource] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [firstNameEnabled, setfirstNameEnabled] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Graph API templates
  const [templateOptions, setTemplateOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const isEditing = type === "edit";
  const currentNameNorm = norm(templateData.template_name || templateData.name);
  const chosenNameNorm = norm(templateName);

  // Duplicate name detection: disallow if name is used by another template.
  const isDuplicateName =
    !!chosenNameNorm &&
    usedTemplateNameSet.has(chosenNameNorm) &&
    (!isEditing || chosenNameNorm !== currentNameNorm);

  // When dialog opens: initialize form + fetch WhatsApp Business templates
  useEffect(() => {
    if (!open) return;

    setTemplateName(templateData.template_name || "");
    setCampaignUrl(templateData.campaign_url || "");
    setUtmSource(templateData.utm_source || "");
    setUtmCampaign(templateData.utm_campaign || "");
    setfirstNameEnabled(!!templateData.first_name_enabled);
    setImageFile(null);
    setIsUploading(false);

    setLoadingOptions(true);
    fetch(
      `https://graph.facebook.com/${GRAPH_API_VERSION}/${BUSINESS_ID}/message_templates` +
        `?status=APPROVED&limit=100&access_token=${GRAPH_TOKEN}`
    )
      .then((res) => res.json())
      .then((json) => {
        if (Array.isArray(json.data)) {
          setTemplateOptions(json.data);
        } else {
          console.error("Graph payload error:", json);
          setTemplateOptions([]);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch templates:", err);
        setTemplateOptions([]);
      })
      .finally(() => setLoadingOptions(false));
  }, [open, templateData]);

  // Only show options that are not already used, except allow the current template's name while editing
  const filteredOptions = useMemo(() => {
    return (templateOptions || []).filter((o) => {
      const n = norm(o?.name);
      if (!n) return false;
      if (isEditing && n === currentNameNorm) return true;
      return !usedTemplateNameSet.has(n);
    });
  }, [templateOptions, usedTemplateNameSet, isEditing, currentNameNorm]);

  const handleImageChange = (e) => {
    const f = e.target.files?.[0];
    if (f) setImageFile(f);
  };

  const handleSaveClick = async () => {
    setIsUploading(true);
    try {
      await onSave({
        ...templateData,
        name: templateName,
        campaign_url: campaignUrl,
        utm_source: utmSource,
        utm_campaign: utmCampaign,
        first_name_enabled: firstNameEnabled,
        image: imageFile,
      });
      onClose();
    } catch (_) {
      // parent shows snackbar
    } finally {
      setIsUploading(false);
    }
  };

  // Required fields + no duplicate name
  const canSave =
    templateName.trim() !== "" &&
    !isDuplicateName &&
    utmSource.trim() !== "" &&
    utmCampaign.trim() !== "" &&
    (imageFile !== null || !!templateData.image_url) &&
    !isUploading;

  if (!open) return null;

  return (
    <>
      <Dialog open fullWidth onClose={() => !isUploading && onClose()}>
        <DialogTitle>{isEditing ? "Edit Template" : "Create Template"}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {/* existing media preview (image or video) */}
            {!imageFile && templateData.image_url && (
              <Box sx={{ textAlign: "center" }}>
                {/\.(mp4)(\?.*)?$/i.test(templateData.image_url) ? (
                  <video
                    src={templateData.image_url}
                    style={{ maxWidth: 200, maxHeight: 200, objectFit: "cover" }}
                    controls
                    muted
                  />
                ) : (
                  <img
                    src={templateData.image_url}
                    alt="thumb"
                    style={{ maxWidth: 200, maxHeight: 200, objectFit: "cover" }}
                  />
                )}
              </Box>
            )}

            {/* pick one of your Meta-registered message templates */}
            <Autocomplete
              options={filteredOptions}
              getOptionLabel={(o) => o.name}
              loading={loadingOptions}
              value={filteredOptions.find((o) => o.name === templateName) || null}
              onChange={(_, sel) => setTemplateName(sel?.name || "")}
              // prevent manual text entry to avoid bypassing duplicate filter
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Template Name"
                  error={isDuplicateName}
                  helperText={
                    isDuplicateName
                      ? "This template name is already in use."
                      : "Select an approved template from our Snag WhatsApp Business Account."
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingOptions && <CircularProgress size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <TextField
              label="Campaign URL"
              value={campaignUrl}
              onChange={(e) => setCampaignUrl(e.target.value)}
              fullWidth
              helperText='The campaign URL is whatever comes after www.snagtights.com/ in the URL but before the UTM terms. For example "collections/all-chub-rub-shorts"'
            />

            <TextField
              label="UTM Source"
              value={utmSource}
              onChange={(e) => setUtmSource(e.target.value)}
              fullWidth
            />

            <TextField
              label="UTM Campaign"
              value={utmCampaign}
              onChange={(e) => setUtmCampaign(e.target.value)}
              fullWidth
              helperText="UTM terms used for tracking of user clicks."
            />

            {/* First Name Enabled with grey label + helper text */}
            <FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={firstNameEnabled}
                    onChange={(e) => setfirstNameEnabled(e.target.checked)}
                  />
                }
                label="First Name Enabled"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    color: "text.secondary",
                  },
                }}
              />
              <FormHelperText>
                Template contains a parameter to insert the first name of the recipient (if available) 
              </FormHelperText>
            </FormControl>

            <Button variant="outlined" component="label" disabled={isUploading}>
              {imageFile || templateData.image_url ? "Change Media (.jpg/.png/.mp4)" : "Upload Media (.jpg/.png/.mp4)"}
              <input
                type="file"
                hidden
                accept=".jpg,.jpeg,.png,.mp4"
                onChange={handleImageChange}
              />
            </Button>
            {imageFile && <Typography>{imageFile.name}</Typography>}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button onClick={handleSaveClick} variant="contained" disabled={!canSave}>
            {isEditing ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        open={isUploading}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}

export default React.memo(WhatsAppTemplatesDialog);
